import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { rem } from '@mantine/core';

import { ViewAsContext, ViewAsContextValue } from './context';
import {
  VIEW_AS_STORAGE_EVENT_KEY,
  ViewAsUser,
  getViewAsUser,
  refreshFromStorage,
  setViewAsUser,
} from './store';
import { VIEW_AS_BANNER_HEIGHT } from '@features/User/ViewAsBanner/styles/index.css';
import { reconnectWsClient } from '@services/apollo/ApolloWrapper/wsClientRegistry';

const VIEW_AS_BANNER_CSS_VAR = '--view-as-banner-height';

function ViewAsProvider({ children }: PropsWithChildren) {
  const apolloClient = useApolloClient();
  const [viewAsUser, setLocalViewAsUser] = useState<ViewAsUser | null>(() =>
    getViewAsUser(),
  );

  // Cross-tab sync: when localStorage changes from another tab, update local
  // state, force the WS to reconnect with the new view-as header, then refresh
  // the Apollo cache (otherwise queries cached under the prior identity stay
  // visible until refetch). Reconnect MUST run before resetStore — resetStore
  // re-subscribes active subscriptions, and we don't want that to happen on
  // the stale connection.
  useEffect(() => {
    const handleStorage = async (event: StorageEvent) => {
      if (event.key !== VIEW_AS_STORAGE_EVENT_KEY) return;
      const next = refreshFromStorage();
      setLocalViewAsUser(next);
      reconnectWsClient();
      try {
        await apolloClient.resetStore();
      } catch {
        // see enterViewAs
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [apolloClient]);

  // Expose the banner height as a global CSS variable on the document root so
  // that fixed-position elements rendered via portals (Mantine notifications,
  // modals, etc. — mounted on document.body, outside the React tree) can
  // offset themselves to stay clear of the banner. Using rem() keeps this in
  // lockstep with the banner's own `height: rem(VIEW_AS_BANNER_HEIGHT)`.
  useEffect(() => {
    const root = document.documentElement;
    if (viewAsUser) {
      root.style.setProperty(
        VIEW_AS_BANNER_CSS_VAR,
        rem(VIEW_AS_BANNER_HEIGHT),
      );
    } else {
      root.style.removeProperty(VIEW_AS_BANNER_CSS_VAR);
    }
    return () => {
      root.style.removeProperty(VIEW_AS_BANNER_CSS_VAR);
    };
  }, [viewAsUser]);

  const enterViewAs = useCallback(
    async (user: ViewAsUser) => {
      setViewAsUser(user);
      setLocalViewAsUser(user);
      // Force the WS to reconnect so subsequent subscriptions carry the
      // view-as header (connectionParams is only evaluated on (re)connect).
      // MUST run before resetStore — resetStore re-subscribes active
      // subscriptions, and we don't want that to happen on the stale connection
      // (still authenticated as the admin).
      reconnectWsClient();
      try {
        await apolloClient.resetStore();
      } catch {
        // resetStore rejects if any active query errors during refetch; the
        // cache is still cleared so it's safe to ignore.
      }
    },
    [apolloClient],
  );

  const exitViewAs = useCallback(async () => {
    // Clear the singleton first so the upcoming refetches drop the
    // view-as header and run with the admin's ACL.
    setViewAsUser(null);
    // Reconnect the WS before resetStore so re-subscriptions don't briefly
    // run on the stale connection (still carrying the view-as header).
    reconnectWsClient();
    try {
      // Wait for the cache to be repopulated with the admin's data BEFORE
      // flipping React state. Otherwise there's a brief window where
      // isViewingAs=false but cached queries (e.g. `me`) still return the
      // impersonated user — which can flash UI gated on that data, like the
      // onboarding modal that fires when firstName/lastName are missing.
      await apolloClient.resetStore();
    } catch {
      // see enterViewAs
    }
    setLocalViewAsUser(null);
  }, [apolloClient]);

  const value = useMemo<ViewAsContextValue>(
    () => ({
      viewAsUser,
      isViewingAs: viewAsUser !== null,
      enterViewAs,
      exitViewAs,
    }),
    [viewAsUser, enterViewAs, exitViewAs],
  );

  return (
    <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>
  );
}

export default ViewAsProvider;
